/**
 * Axes no plot reads — dropped when a plot goes, and swept once on load
 * (owner, 2026-10-31).
 *
 * An overlay moved onto a scale of its own draws a *visible* axis beside the
 * price one (left or right). Until 2026-10-31 removing the overlay dropped its
 * axis only when that axis was hidden (Volume on price), so a saved chart can
 * still carry an empty left scale with nothing drawn on it. Removal now prunes
 * every unread axis (`add-indicator.ts`), and `pruneOrphanAxes` clears the ones
 * saved before that, as the workspace loads. Saved with the next edit, like
 * every other load-time upgrade.
 *
 * **The first axis always stays**: a plot with no `axisRef` reads it, and the
 * price pane is never without one. Only plots name an axis (`Plot.axisRef`), so
 * an axis none of them names draws nothing at all.
 *
 * Pure. Unchanged subtrees keep their identity, so a workspace with nothing to
 * sweep comes back as itself.
 */
import type { ChartGroup, Pane, Plot, UserWorkspace } from "@qufolio/model";

/** `pane` with `kept` as its plots, and every axis bar the first that none of them reads dropped. */
export function withoutUnusedAxes(pane: Pane, kept: Plot[]): Pane {
  const all = pane.axes ?? [];
  const first = all[0]?.id;
  const used = new Set(kept.map((plot) => plot.axisRef ?? first));
  const axes = all.filter((a, i) => i === 0 || used.has(a.id));
  return axes.length === all.length ? { ...pane, plots: kept } : { ...pane, plots: kept, axes };
}

function pruneGroup(group: ChartGroup): ChartGroup {
  let changed = false;
  const panes = group.panes.map((pane) => {
    const next = withoutUnusedAxes(pane, pane.plots ?? []);
    if (next.axes === pane.axes) return pane;
    changed = true;
    return next;
  });
  return changed ? { ...group, panes } : group;
}

/** The workspace with every chart's unread axes dropped; itself if there are none. */
export function pruneOrphanAxes(workspace: UserWorkspace): UserWorkspace {
  let changed = false;
  const layouts = workspace.layouts.map((layout) => {
    let layoutChanged = false;
    const sections = layout.sections.map((section) => {
      if (section.kind !== "chartGroup") return section;
      const group = section.config as ChartGroup;
      if (!Array.isArray(group?.panes)) return section;
      const next = pruneGroup(group);
      if (next === group) return section;
      layoutChanged = true;
      return { ...section, config: next };
    });
    if (!layoutChanged) return layout;
    changed = true;
    return { ...layout, sections };
  });
  return changed ? { ...workspace, layouts } : workspace;
}
